import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  types,
  loadUsers,
  loadUsersResponse,
  createUserResponse,
  deleteUserResponse,
  setUserPermissionResponse,
  showEditUser,
  hideEditUser,
  updateAdminSettings,
} from '@actions/admin';
import {
  createUser,
  deleteUser,
  getUsers,
  updatePermission,
  getAdminSettings,
  changeAdminSettings,
  transformSettingsResponse,
  transformSettingsArrayToObject,
  forceSync,
} from '@services/admin';
import {
  selectCreateUserForm,
  selectPermissionsFormUserId,
  selectCurrentUserPermission,
  selectPermissionsFormSelectedPermission,
} from '@selectors/admin';
import { tokenSelector } from '@selectors/authentication';
import { displaySnackBar } from '@actions/snackbar';

const shouldDeleteOldConferencesKey = 'shouldDeleteOldConferences';

export function* getUsersSaga(action) {
  try {
    const token = yield select(tokenSelector);
    const usersResponse = yield call(getUsers, token);
    const users = usersResponse.data.map(({ email, id, roles }) =>
      roles.length > 0
        ? { email, id, role: roles[0].name }
        : { email, id, role: 'Undefined' },
    );
    yield put(loadUsersResponse(users, false));
  } catch (e) {
    yield put(loadUsersResponse(undefined, true));
  }
}

export function* createUserSaga(action) {
  try {
    const token = yield select(tokenSelector);
    const { email, password } = yield select(selectCreateUserForm);
    yield call(createUser, token, email, password);
    yield put(createUserResponse());
    yield put(loadUsers());
  } catch (e) {
    yield put(createUserResponse(true));
  }
}

export function* deleteUserSaga(action) {
  try {
    const id = action.payload;
    const token = yield select(tokenSelector);
    const ans = window.confirm('Do you really want to delete this user?');
    if (ans !== true) {
      throw new Error('User aborted deleteUser operation via confirm prompt.');
    }
    yield call(deleteUser, token, id);
    yield put(deleteUserResponse());
    yield put(loadUsers());
  } catch (e) {
    yield put(deleteUserResponse(true));
  }
}

export function* deleteUserSnackbarSaga(action) {
  if (!action.error) {
    yield put(displaySnackBar(true, 'Successfully deleted user'));
  } else {
    yield put(displaySnackBar(false, 'Error while deleting the user'));
  }
}

export function* createUserSnackbarSaga(action) {
  if (!action.error) {
    yield put(displaySnackBar(true, 'Successfully created user'));
  } else {
    yield put(displaySnackBar(false, 'Error while creating the user'));
  }
}

export function* setEditUserSaga(action) {
  try {
    const role = yield select(selectPermissionsFormSelectedPermission);
    const userId = yield select(selectPermissionsFormUserId);
    const token = yield select(tokenSelector);
    yield call(updatePermission, token, userId, role);
    yield put(setUserPermissionResponse());
    yield put(loadUsers());
  } catch (e) {
    yield put(setUserPermissionResponse(true));
  }
}

export function* setEditUserSnackbarSaga(action) {
  if (!action.error) {
    yield put(displaySnackBar(true, 'Successfully updated user permissions'));
  } else {
    yield put(displaySnackBar(false, 'Error while updating user permissions'));
  }
}

// TODO: #649 It's way too complicated. Try to simplify this.
export function* toggleEditUserForm(action) {
  const current = yield select(selectPermissionsFormUserId);
  const userId = action.payload;
  if (current !== action.payload) {
    const permission = yield select(selectCurrentUserPermission, userId);
    yield put(showEditUser({ userId, permission }));
  } else {
    yield put(hideEditUser());
  }
}

export function* getAdminSettingsSaga(action) {
  try {
    const token = yield select(tokenSelector);
    const { data } = yield call(getAdminSettings, token);
    yield put(updateAdminSettings(transformSettingsArrayToObject(data)));
  } catch (err) {
    yield put(displaySnackBar(false, err.message || 'Error while getting settings'));
  }
}

const isDeleteOldTranscriptsSuspended = (settings) =>
  _.get(settings, 'key') === shouldDeleteOldConferencesKey &&
  _.get(settings, 'value') === false;

export function* changeAdminSettingsSaga(action) {
  try {
    const token = yield select(tokenSelector);
    const { data } = yield call(changeAdminSettings, token, action.settings);
    yield put(updateAdminSettings(transformSettingsResponse(data)));
    yield put(displaySnackBar(true, 'Successfuly changed settings'));
  } catch (err) {
    yield put(
      displaySnackBar(false, err.message || 'Error while trying to change setting'),
    );
  }
}

export function* forceSyncRequestSaga(action) {
  try {
    const token = yield select(tokenSelector);
    yield call(forceSync, token);
    yield put(displaySnackBar(true, 'Force Sync requested'));
  } catch (err) {
    yield put(displaySnackBar(false, 'Force Sync failed'));
  }
}

export default function* adminWatcherSaga() {
  yield takeEvery(types.LOAD_USERS.REQUEST, getUsersSaga);
  yield takeEvery(types.CREATE_USER.REQUEST, createUserSaga);
  yield takeEvery(types.DELETE_USER.REQUEST, deleteUserSaga);
  yield takeEvery(types.CREATE_USER.RESPONSE, createUserSnackbarSaga);
  yield takeEvery(types.DELETE_USER.RESPONSE, deleteUserSnackbarSaga);
  yield takeEvery(types.EDIT_USER.REQUEST, setEditUserSaga);
  yield takeEvery(types.EDIT_USER.RESPONSE, setEditUserSnackbarSaga);
  yield takeEvery(types.EDIT_USER.TOGGLE, toggleEditUserForm);
  yield takeEvery(types.CHANGE_ADMIN_SETTINGS, changeAdminSettingsSaga);
  yield takeEvery(types.FORCE_SYNC.REQUEST, forceSyncRequestSaga);
}



// WEBPACK FOOTER //
// ./redux/sagas/admin.js